import React, { ChangeEvent } from 'react';
import PageSection from 'components/PageSection';
import GridContainer from 'components/GridContainer';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  styled,
  Typography,
  Tooltip,
  useTheme,
  Grid,
  Menu,
  MenuItem,
} from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import Chip from 'components/Chip';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { CheckCircleOutline, ErrorOutline } from '@mui/icons-material';
import {
  numberToCurrencyString,
  numberToDisplayString,
  numberToPercentageStringWithLimits,
} from 'further-ui/utils';
import AppTable from 'components/Table';
import { round, sumBy } from 'lodash';
import { Api } from 'further-types/allocations';
import { FundAllocation } from '../index';
import getTotalValuesForField from './get-total-values-for-field';
import AppNumberInput from 'components/FormElements/AppNumberInput';
import {
  Controller,
  Control,
  useWatch,
  UseFormSetValue,
} from 'react-hook-form';
import Button from 'components/Button';
import ButtonGroup from 'components/ButtonGroup';
import EditedTotals from './EditedTotals';
import FinalDealToggleBar from './FinalDealToggleBar';
import FundEditHeaderbar from './FundEditHeaderbar';
import EditActionsButtons from './EditActionsButtons';
import { SubscriptionAsInvestorLink } from 'components/Links';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';

type FormValues = {
  fundAllocations: Array<FundAllocation>;
};

type ReviewFundSectionProps = {
  formFundAllocations: Array<FundAllocation>;
  originalFundAllocations: Array<FundAllocation>;
  existingAllocationId?: string;
  draftAllocationId?: string;
  isEditing: boolean;
  control: Control<FormValues>;
  setValue: UseFormSetValue<FormValues>;
  setIsEditing: (isEditing: boolean) => void;
  containsExitedShareholdings: boolean;
  fundIdsInDraftAllocation: Array<string>;
  containsExportedTransactionFees: boolean;
  isShareConsiderationAllocation?: boolean;
  isAutoAllocation: boolean;
  onReprocessAllocation: () => void;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
  onMenuClose: () => void;
  amendmentsMenuAnchorEl: HTMLElement | null;
  onDownloadAllocationFile: () => void;
  onFileInputChange: (e: ChangeEvent<HTMLInputElement>) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onDismissEdit: () => void;
  onSaveEdit: () => void;
  saveEditDisabled?: boolean;
  saveEditDisabledTooltip?: string;
  saveEditLoading?: boolean;
  hasAmendmentsFile?: boolean;
  onFundModified: (fundIndex: number) => void;
};

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  width: '100%',
  border: `1px solid ${theme.palette.borderColor?.lightGrey}`,
}));

const StyledChipContainer = styled('div')({
  height: '23px',
});

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  letterSpacing: '0',
  fontSize: theme.fontSizes?.regular,
}));

const StyledCheckCircleOutline = styled(CheckCircleOutline)(({ theme }) => ({
  fontSize: '23px',
  marginLeft: theme.spacing(2),
}));

const StyledErrorOutline = styled(ErrorOutline)<{ inTable?: boolean }>(
  ({ theme, inTable }) => ({
    fontSize: inTable ? '18px' : '23px',
    paddingBottom: inTable ? '1px' : 0,
    marginLeft: theme.spacing(2),
  }),
);

const AccordionHeader = styled(AccordionSummary)(({ theme }) => ({
  padding: theme.spacing(4),
  color: theme.palette.text.darkTitle,
  background: 'rgba(234,233,232,0.2)',
  fontSize: theme.fontSizes?.regular,
  fontWeight: 'bold',

  '& .MuiAccordionSummary-content': {
    margin: '0 !important',
  },
}));

const AccordionBody = styled(AccordionDetails)(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  padding: 0,
  paddingBottom: theme.spacing(10),
}));

const CenteredTypography = styled(Typography)({
  fontWeight: 'bold',
  display: 'flex',
  alignItems: 'center',
  paddingTop: '2px',
});

const CenteredGrid = styled(Grid)<{
  justifyContent?: 'center' | 'flex-end' | 'flex-start';
}>(({ justifyContent }) => ({
  display: 'flex',
  justifyContent: justifyContent ?? 'center',
  alignItems: 'center',
}));

const StyledTotalsRow = styled('div')(({ theme }) => ({
  padding: theme.spacing(4),
  color: theme.palette.text.darkTitle,
  background: 'rgba(234,233,232,0.2)',
  fontSize: theme.fontSizes?.sm,
  border: `1px solid ${theme.palette.borderColor?.lightGrey}`,
}));

const WideColumnLabel = styled('div')<{ medium: boolean }>(
  ({ medium = false }) => ({
    width: medium ? '100px' : '150px',
    wordWrap: 'break-word',
    whiteSpace: 'normal',
  }),
);

const StyledInfoOutlined = styled(InfoOutlined)({
  fontSize: '18px',
  paddingBottom: '1px',
});

const DeployStatusBar = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.grey[200],
  fontSize: theme.fontSizes?.regular,
  padding: theme.spacing(4),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  border: `1px solid ${theme.palette.borderColor?.lightGrey}`,
  marginBottom: theme.spacing(2),
}));

const StyledLink = styled('span')(({ theme }) => ({
  color: theme.palette.primary.main,
  textDecoration: 'underline',
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

const FundSection: React.FC<ReviewFundSectionProps> = ({
  formFundAllocations,
  originalFundAllocations,
  existingAllocationId,
  draftAllocationId,
  isEditing,
  control,
  setValue,
  setIsEditing,
  containsExitedShareholdings,
  fundIdsInDraftAllocation,
  containsExportedTransactionFees,
  isShareConsiderationAllocation = false,
  isAutoAllocation,
  onReprocessAllocation,
  onMenuOpen,
  onMenuClose,
  amendmentsMenuAnchorEl,
  onDownloadAllocationFile,
  onFileInputChange,
  fileInputRef,
  onDismissEdit,
  onSaveEdit,
  saveEditDisabled,
  saveEditDisabledTooltip,
  saveEditLoading,
  hasAmendmentsFile,
  onFundModified,
}) => {
  const [canEditAllocation] = useHasPermissions([
    RolePermission.EditAllocationUpload,
  ]);
  const isExistingAllocation = !!existingAllocationId;
  const isDraftAllocation = !!draftAllocationId;

  const handleInputClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
      onMenuClose();
    }
  };

  // Watch all form values for totals calculation
  const watchedValues = useWatch({ control }) as FormValues;
  const theme = useTheme();

  // Check if all funds contain frozen investments
  const allFundsContainFrozenInvestments = formFundAllocations.every(
    (fundAllocation) =>
      fundAllocation.investments?.some((investment) => investment.isFrozen) ??
      false,
  );

  // Determine if editing should be disabled
  const isEditDisabled =
    containsExitedShareholdings ||
    containsExportedTransactionFees ||
    allFundsContainFrozenInvestments ||
    isShareConsiderationAllocation;

  // Get the tooltip message for disabled edit buttons
  const getEditDisabledTooltip = () => {
    if (isShareConsiderationAllocation) {
      return 'An allocation as a result of a share consideration exit cannot be edited.';
    }
    if (containsExitedShareholdings) {
      return "As there has been a subsequent exit of some or all of this allocation's shares, this tranche allocation is uneditable. Delete the relevant exit if you wish to edit this allocation.";
    }
    if (containsExportedTransactionFees) {
      return 'As transaction fees related to this allocation have been exported to the custodian, the tranche allocation is not editable.';
    }
    if (allFundsContainFrozenInvestments) {
      return 'All funds in this allocation contain frozen investments and cannot be edited.';
    }
    return undefined;
  };

  return (
    <PageSection>
      {/* View state buttons - for all allocations */}
      {!isEditing && canEditAllocation && (
        <ButtonGroup align="right" marginBottom={theme.spacing(7)}>
          <Button
            variant="outlined"
            onClick={onMenuOpen}
            disabled={isEditDisabled}
            tooltip={getEditDisabledTooltip()}
          >
            Edit in spreadsheet
          </Button>
          <Button
            disabled={isEditDisabled}
            variant="outlined"
            onClick={() => setIsEditing(true)}
            tooltip={getEditDisabledTooltip()}
          >
            Edit in page
          </Button>
        </ButtonGroup>
      )}

      {/* Edit state buttons - for all allocations */}
      {isEditing && (
        <EditActionsButtons
          onDismissEdit={onDismissEdit}
          onSaveEdit={onSaveEdit}
          disabled={saveEditDisabled}
          disabledTooltip={saveEditDisabledTooltip}
          loading={saveEditLoading}
          align="right"
          marginBottom={theme.spacing(7)}
          showReupload={hasAmendmentsFile}
          onReupload={handleInputClick}
        />
      )}

      {/* Spreadsheet edit menu */}
      <Menu
        anchorEl={amendmentsMenuAnchorEl}
        keepMounted
        open={Boolean(amendmentsMenuAnchorEl)}
        onClose={onMenuClose}
      >
        <StyledMenuItem onClick={onDownloadAllocationFile}>
          Download allocation file
        </StyledMenuItem>
        <StyledMenuItem onClick={handleInputClick}>
          Upload amended allocation file
        </StyledMenuItem>
      </Menu>

      {/* Hidden file input for uploads */}
      <input
        style={{ display: 'none' }}
        ref={fileInputRef}
        type="file"
        onChange={(e) => {
          onFileInputChange(e);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }}
      />
      {formFundAllocations.map((fundAllocation, fundIndex) => {
        // Get current fund allocation from form data
        const currentFundAllocation =
          watchedValues?.fundAllocations?.[fundIndex] ?? fundAllocation;

        // Check if any investments in this fund are frozen
        const hasFrozenInvestments =
          currentFundAllocation.investments?.some(
            (investment) => investment.isFrozen,
          ) ?? false;

        const canEdit =
          !currentFundAllocation.isFullyDeployed &&
          isEditing &&
          !hasFrozenInvestments &&
          !isShareConsiderationAllocation;

        // Define table columns with access to fundIndex
        const columns = [
          {
            label: 'Investor',
            key: 'investorName',
            sort: false,
            render: (row: Api.InvestmentRow) => (
              <WideColumnLabel>
                <SubscriptionAsInvestorLink
                  subscriptionId={row.investmentId}
                  investorFullName={row.investor?.fullName}
                />
              </WideColumnLabel>
            ),
          },
          {
            label: (
              <WideColumnLabel>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Pre-allocation deployable capital
                  {canEdit && (isDraftAllocation || isExistingAllocation) && (
                    <Tooltip
                      arrow
                      title="This is the investor's current capital awaiting investment (including the amount allocated to this investment). If there have been new allocations since this allocation was first saved, the capital allocated to those allocations has been removed from this figure to give you an up-to-date number."
                    >
                      <StyledInfoOutlined />
                    </Tooltip>
                  )}
                </Box>
              </WideColumnLabel>
            ),
            key: 'investmentCapitalRemaining',
            sort: false,
            render: (row: Api.InvestmentRow, rowIndex: number) => {
              if (isShareConsiderationAllocation && !isEditing) {
                return '-';
              }

              const currentInvestment =
                currentFundAllocation.investments?.[rowIndex]
                  ?.adjustedNetInvestmentExFee ?? 0;
              const currentTransactionFee =
                currentFundAllocation.investments?.[rowIndex]
                  ?.transactionFeeAmount ?? 0;
              const totalCurrentInvestment =
                currentInvestment + currentTransactionFee;

              const originalInvestmentForComparison = originalFundAllocations[
                fundIndex
              ]?.investments?.find((inv) =>
                row.shareAllocationUploadLogId
                  ? inv.shareAllocationUploadLogId ===
                    row.shareAllocationUploadLogId
                  : inv.investorId === row.investorId &&
                    inv.investmentId === row.investmentId,
              );
              const comparisonValue = isExistingAllocation
                ? (row.heldByFundAwaitingInvestment ?? 0) +
                  (originalInvestmentForComparison?.adjustedNetInvestmentExFee ??
                    0) +
                  (originalInvestmentForComparison?.transactionFeeAmount ?? 0)
                : row.heldByFundAwaitingInvestment ?? 0;

              return (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {numberToCurrencyString(
                    isExistingAllocation && !canEdit
                      ? row.priorToAllocationValues
                          ?.investmentCapitalRemaining ?? 0
                      : row.investmentCapitalRemaining,
                  )}

                  {totalCurrentInvestment > comparisonValue &&
                    !row.isFrozen && (
                      <Tooltip
                        arrow
                        title={
                          totalCurrentInvestment >
                          (row.investmentCapitalRemaining ?? 0)
                            ? 'This amount is in excess of what the investor has remaining available for investment.'
                            : 'This allocation requires unpaid committed capital to be paid by this investor prior to investment. Please request unpaid funds before completing this allocation.'
                        }
                      >
                        <StyledErrorOutline inTable={true} color="error" />
                      </Tooltip>
                    )}
                </Box>
              );
            },
          },
          {
            label: 'Shares allocated',
            key: 'sharesAllocated',
            sort: false,
            render: (_row: Api.InvestmentRow, rowIndex: number) => {
              if (canEdit) {
                return (
                  <Controller
                    control={control}
                    name={`fundAllocations.${fundIndex}.investments.${rowIndex}.finalShareholding`}
                    render={({ field }) => {
                      const dealSharePrice =
                        currentFundAllocation?.dealSharePrice ?? 0;

                      return (
                        <AppNumberInput
                          value={field.value}
                          onChange={(value) => {
                            const previousValue = field.value;
                            field.onChange(value);

                            // Only update the adjusted investment if the value actually changed
                            if (value !== previousValue) {
                              // Check if adjusted investment has been manually edited
                              const currentAdjustedInvestment =
                                currentFundAllocation?.investments?.[rowIndex]
                                  ?.adjustedNetInvestmentExFee ?? 0;
                              const originalAdjustedInvestment =
                                originalFundAllocations[fundIndex]
                                  ?.investments?.[rowIndex]
                                  ?.adjustedNetInvestmentExFee ?? 0;

                              // Only auto-update adjusted investment if it hasn't been manually edited
                              const hasBeenManuallyEdited =
                                currentAdjustedInvestment !==
                                originalAdjustedInvestment;

                              if (!hasBeenManuallyEdited) {
                                const adjustedInvestmentValue = round(
                                  value * dealSharePrice,
                                  2,
                                );
                                setValue(
                                  `fundAllocations.${fundIndex}.investments.${rowIndex}.adjustedNetInvestmentExFee`,
                                  adjustedInvestmentValue,
                                );
                                onFundModified(fundIndex);
                              }
                            }
                          }}
                          size="small"
                          fullWidth={false}
                          decimals={0}
                        />
                      );
                    }}
                  />
                );
              }
              const finalShareholding =
                currentFundAllocation?.investments?.[rowIndex]
                  ?.finalShareholding;
              return numberToDisplayString(finalShareholding ?? 0);
            },
          },
          // Only show "Allocated investment" column if not editing, or if editing an auto allocation
          ...(!isEditing || (isEditing && isAutoAllocation)
            ? [
                {
                  label: 'Allocated investment',
                  key: 'adjustedNetInvestmentExFee',
                  sort: false,
                  render: (_row: Api.InvestmentRow, rowIndex: number) => {
                    if (!isEditing) {
                      // When not editing, show the current adjusted investment value
                      const adjustedNetInvestmentExFee =
                        currentFundAllocation?.investments?.[rowIndex]
                          ?.adjustedNetInvestmentExFee ?? 0;
                      return numberToCurrencyString(adjustedNetInvestmentExFee);
                    } else {
                      // When editing (auto allocation only), show the original value from AA as reference
                      const currentInvestment =
                        currentFundAllocation?.investments?.[rowIndex];
                      const originalInvestment = originalFundAllocations[
                        fundIndex
                      ]?.investments?.find((inv) =>
                        currentInvestment?.shareAllocationUploadLogId
                          ? inv.shareAllocationUploadLogId ===
                            currentInvestment?.shareAllocationUploadLogId
                          : inv.investorId === currentInvestment?.investorId &&
                            inv.investmentId ===
                              currentInvestment?.investmentId,
                      );
                      const originalAdjustedNetInvestmentExFee =
                        originalInvestment?.adjustedNetInvestmentExFee ?? 0;
                      return numberToCurrencyString(
                        originalAdjustedNetInvestmentExFee,
                      );
                    }
                  },
                },
              ]
            : []),
          ...(canEdit
            ? [
                {
                  label: 'Adjusted investment',
                  key: 'amountInvestedBasedOnShareholding',
                  sort: false,
                  render: (_row: Api.InvestmentRow, rowIndex: number) => {
                    if (isShareConsiderationAllocation && !isEditing) {
                      return '-';
                    }

                    return (
                      <Controller
                        control={control}
                        name={`fundAllocations.${fundIndex}.investments.${rowIndex}.adjustedNetInvestmentExFee`}
                        render={({ field }) => {
                          const currentValue = field.value ?? 0;

                          return (
                            <AppNumberInput
                              value={currentValue}
                              onChange={(value) => {
                                field.onChange(value);
                                onFundModified(fundIndex);
                              }}
                              currencyStartAdornment
                              size="small"
                              fullWidth={false}
                              decimals={2}
                            />
                          );
                        }}
                      />
                    );
                  },
                },
              ]
            : []),
          ...(canEdit
            ? [
                {
                  label: 'Transaction fee (excl. VAT)',
                  key: 'transactionFeeNet',
                  render: (_row: Api.InvestmentRow, rowIndex: number) => {
                    if (isShareConsiderationAllocation && !isEditing) {
                      return '-';
                    }

                    return (
                      <Controller
                        control={control}
                        name={`fundAllocations.${fundIndex}.investments.${rowIndex}.transactionFeeNet`}
                        render={({ field }) => {
                          const netValue = field.value ?? 0;

                          return (
                            <AppNumberInput
                              value={netValue}
                              onChange={(value) => {
                                field.onChange(value);
                                // Update total transaction fee (gross = net + vat)
                                const currentInv =
                                  currentFundAllocation?.investments?.[
                                    rowIndex
                                  ];
                                const vat = currentInv?.transactionFeeVat ?? 0;
                                setValue(
                                  `fundAllocations.${fundIndex}.investments.${rowIndex}.transactionFeeAmount`,
                                  round(value + vat, 2),
                                );
                                onFundModified(fundIndex);
                              }}
                              currencyStartAdornment
                              size="small"
                              fullWidth={false}
                              decimals={2}
                            />
                          );
                        }}
                      />
                    );
                  },
                },
                {
                  label: <WideColumnLabel medium={true}>VAT</WideColumnLabel>,
                  key: 'transactionFeeVat',
                  render: (_row: Api.InvestmentRow, rowIndex: number) => {
                    if (isShareConsiderationAllocation && !isEditing) {
                      return '-';
                    }

                    return (
                      <Controller
                        control={control}
                        name={`fundAllocations.${fundIndex}.investments.${rowIndex}.transactionFeeVat`}
                        render={({ field }) => {
                          const currentValue = field.value ?? 0;

                          return (
                            <AppNumberInput
                              value={currentValue}
                              onChange={(value) => {
                                field.onChange(value);
                                // Update total transaction fee (gross = net + vat)
                                const currentInv =
                                  currentFundAllocation?.investments?.[
                                    rowIndex
                                  ];
                                const net = currentInv?.transactionFeeNet ?? 0;
                                setValue(
                                  `fundAllocations.${fundIndex}.investments.${rowIndex}.transactionFeeAmount`,
                                  round(net + value, 2),
                                );
                                onFundModified(fundIndex);
                              }}
                              currencyStartAdornment
                              size="small"
                              fullWidth={false}
                              decimals={2}
                            />
                          );
                        }}
                      />
                    );
                  },
                },
              ]
            : [
                {
                  label: 'Transaction fee',
                  key: 'transactionFee',
                  render: (row: Api.InvestmentRow, rowIndex: number) => {
                    const transactionFeeAmount =
                      currentFundAllocation?.investments?.[rowIndex]
                        ?.transactionFeeAmount ?? row.transactionFeeAmount;
                    return numberToCurrencyString(transactionFeeAmount);
                  },
                },
              ]),
          {
            label: 'Allocation %',
            key: 'allocationPercentage',
            sort: false,
            render: (row: Api.InvestmentRow, rowIndex: number) => {
              if (isShareConsiderationAllocation && !isEditing) {
                return '-';
              }

              const adjustedInvestment =
                currentFundAllocation?.investments?.[rowIndex]
                  ?.adjustedNetInvestmentExFee ??
                row.adjustedNetInvestmentExFee;

              const totalInvestmentIncFee =
                adjustedInvestment +
                (currentFundAllocation?.investments?.[rowIndex]
                  ?.transactionFeeAmount ?? 0);

              // Calculate allocation % as percentage of total investable capital
              // (subscription net of cash fees), not remaining capital
              const allocationPercent =
                row.totalTrancheInvestmentCapital > 0
                  ? (totalInvestmentIncFee /
                      row.totalTrancheInvestmentCapital) *
                    100
                  : 0;

              return numberToPercentageStringWithLimits(allocationPercent);
            },
          },
          {
            label: (
              <WideColumnLabel>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  Post-allocation deployable capital
                  {canEdit && (draftAllocationId || existingAllocationId) && (
                    <Tooltip
                      arrow
                      title="This is the investor's current capital awaiting investment (including the amount allocated to this investment). If there have been new allocations since this allocation was first saved, the capital allocated to those allocations has been removed from this figure to give you an up-to-date number."
                    >
                      <StyledInfoOutlined />
                    </Tooltip>
                  )}
                </Box>
              </WideColumnLabel>
            ),
            key: 'netCapitalRemaining',
            sort: false,
            render: (row: Api.InvestmentRow, rowIndex: number) => {
              if (isShareConsiderationAllocation && !isEditing) {
                return '-';
              }

              const investmentCapitalRemaining =
                isExistingAllocation && !canEdit
                  ? row.priorToAllocationValues?.investmentCapitalRemaining ??
                    row.investmentCapitalRemaining
                  : row.investmentCapitalRemaining;

              const adjustedInvestment =
                currentFundAllocation?.investments?.[rowIndex]
                  ?.adjustedNetInvestmentExFee ??
                row.adjustedNetInvestmentExFee;
              const transactionFee =
                currentFundAllocation?.investments?.[rowIndex]
                  ?.transactionFeeAmount ?? row.transactionFeeAmount;

              const postAllocationCapital = round(
                investmentCapitalRemaining -
                  adjustedInvestment -
                  transactionFee,
                2,
              );

              // Even with rounding postAllocationCapital can return -0 due to floating point precision
              // Negative values are valid here, -0 is not
              const sanitiseNegativeZero =
                postAllocationCapital === 0 ? 0 : postAllocationCapital;

              return numberToCurrencyString(sanitiseNegativeZero);
            },
          },
          {
            label: (
              <WideColumnLabel>
                Post-allocation deployable capital %
              </WideColumnLabel>
            ),
            key: 'percentCapitalRemaining',
            sort: false,
            render: (row: Api.InvestmentRow, rowIndex: number) => {
              if (isShareConsiderationAllocation && !isEditing) {
                return '-';
              }

              // Get the post-allocation deployable capital from the form values
              const investmentCapitalRemaining =
                isExistingAllocation && !canEdit
                  ? row.priorToAllocationValues?.investmentCapitalRemaining ??
                    row.investmentCapitalRemaining
                  : row.investmentCapitalRemaining;

              const adjustedInvestment =
                currentFundAllocation?.investments?.[rowIndex]
                  ?.adjustedNetInvestmentExFee ??
                row.adjustedNetInvestmentExFee;
              const transactionFee =
                currentFundAllocation?.investments?.[rowIndex]
                  ?.transactionFeeAmount ?? row.transactionFeeAmount;
              const transactionFeeVat =
                (transactionFee * (row.defaultTransactionFeeVatPercent || 0)) /
                100;
              const totalTransactionFee = round(
                transactionFee + transactionFeeVat,
                2,
              );

              const postAllocationCapital = round(
                investmentCapitalRemaining -
                  adjustedInvestment -
                  totalTransactionFee,
                2,
              );

              // Calculate percentage as % of total investable capital
              // (total commitment minus cash fees)
              const percentCapitalRemaining =
                row.totalTrancheInvestmentCapital > 0
                  ? (postAllocationCapital /
                      row.totalTrancheInvestmentCapital) *
                    100
                  : 0;

              return numberToPercentageStringWithLimits(
                percentCapitalRemaining,
              );
            },
          },
        ];

        const allSharesAllocated =
          sumBy(
            currentFundAllocation?.investments ?? [],
            'finalShareholding',
          ) >= fundAllocation.targetShares;

        // Check if any shareholdings were limited by single company allocation
        const containsAnyShareholdingsLimitedBySingleCompanyAllocation =
          fundAllocation.containsInvestmentsLimitedBySingleCompanyAllocation &&
          !allSharesAllocated;

        // Don't show warning if uploaded amendment
        const allInvestorsAllocated =
          (currentFundAllocation?.investments ?? []).filter(
            (investment) => (investment.finalShareholding ?? 0) > 0,
          ).length === fundAllocation.investmentsInTranche;

        const anyInvestorsOverAllocated =
          currentFundAllocation.investments?.some((investment) => {
            // Skip frozen investments as funds have been transferred out
            if (investment.isFrozen) {
              return false;
            }

            const originalInvestment = originalFundAllocations[
              fundIndex
            ]?.investments?.find((inv) =>
              investment.shareAllocationUploadLogId
                ? inv.shareAllocationUploadLogId ===
                  investment.shareAllocationUploadLogId
                : inv.investorId === investment.investorId &&
                  inv.investmentId === investment.investmentId,
            );
            return (
              round(
                (investment.adjustedNetInvestmentExFee ?? 0) +
                  (investment.transactionFeeAmount ?? 0),
                2,
              ) >
              (isExistingAllocation
                ? (investment.heldByFundAwaitingInvestment ?? 0) +
                  (originalInvestment?.adjustedNetInvestmentExFee ?? 0) +
                  (originalInvestment?.transactionFeeAmount ?? 0)
                : investment.heldByFundAwaitingInvestment ?? 0)
            );
          });

        const hasAnyError =
          (!allInvestorsAllocated && isAutoAllocation) ||
          (anyInvestorsOverAllocated && isAutoAllocation) ||
          (!allSharesAllocated &&
            !containsAnyShareholdingsLimitedBySingleCompanyAllocation) ||
          containsAnyShareholdingsLimitedBySingleCompanyAllocation;

        return (
          <StyledAccordion
            key={fundAllocation.trancheId}
            slotProps={{ transition: { timeout: 100 } }}
          >
            <AccordionHeader expandIcon={<ExpandMoreIcon color="primary" />}>
              <GridContainer
                style={{
                  width: '100%',
                  margin: 0,
                }}
              >
                <CenteredGrid
                  justifyContent="flex-start"
                  size={{ xs: 12, sm: 12, md: 3 }}
                >
                  <CenteredTypography variant="h6">
                    {fundAllocation.trancheName}
                  </CenteredTypography>
                  {(hasFrozenInvestments || isShareConsiderationAllocation) && (
                    <Tooltip
                      arrow
                      title={
                        isShareConsiderationAllocation
                          ? 'An allocation as a result of a share consideration exit cannot be edited.'
                          : 'This fund contains frozen investments and cannot be edited.'
                      }
                    >
                      <StyledInfoOutlined sx={{ marginLeft: 1 }} />
                    </Tooltip>
                  )}
                </CenteredGrid>
                <CenteredGrid size={{ xs: 12, sm: 12, md: 2 }}>
                  <CenteredTypography variant="h6">
                    Shares:{' '}
                    {numberToDisplayString(
                      sumBy(
                        currentFundAllocation?.investments ?? [],
                        'finalShareholding',
                      ),
                    )}
                    /
                    {numberToDisplayString(
                      originalFundAllocations[fundIndex]?.targetShares ?? 0,
                    )}
                  </CenteredTypography>
                  {!allSharesAllocated &&
                    !isExistingAllocation &&
                    !containsAnyShareholdingsLimitedBySingleCompanyAllocation && (
                      <Tooltip
                        arrow
                        title="All target shares could not be allocated. This is caused by either a high share price, low amount of remaining tranche capital or edits. You may accept the allocation as it is, re-run the allocation with different settings, or manually edit the allocation."
                      >
                        <StyledErrorOutline color="error" />
                      </Tooltip>
                    )}
                  {containsAnyShareholdingsLimitedBySingleCompanyAllocation && (
                    <Tooltip
                      arrow
                      title={
                        <span>
                          All target shares could not be allocated as one or
                          more investors will exceed the set maximum single
                          company allocation. To re-run the allocation,{' '}
                          <StyledLink onClick={onReprocessAllocation}>
                            click here
                          </StyledLink>{' '}
                          to go back to the auto-allocation setting screen,
                          where you can reduce the maximum single company
                          allocation.
                        </span>
                      }
                    >
                      <StyledErrorOutline color="error" />
                    </Tooltip>
                  )}
                </CenteredGrid>
                <CenteredGrid size={{ xs: 12, sm: 12, md: 2 }}>
                  <CenteredTypography variant="h6">
                    Investors:{' '}
                    {numberToDisplayString(
                      (currentFundAllocation?.investments ?? []).filter(
                        (investment) => (investment.finalShareholding ?? 0) > 0,
                      ).length,
                    )}
                    /
                    {numberToDisplayString(fundAllocation.investmentsInTranche)}
                  </CenteredTypography>
                  {!allInvestorsAllocated &&
                    !isExistingAllocation &&
                    isAutoAllocation && (
                      <Tooltip
                        arrow
                        title="The allocation excludes some investors. This is caused by either a high share price or low amount of remaining tranche capital. You may accept the allocation as it is, re-run the allocation with different settings, or manually edit the allocation."
                      >
                        <StyledErrorOutline color="error" />
                      </Tooltip>
                    )}
                </CenteredGrid>
                <CenteredGrid size={{ xs: 12, sm: 12, md: 3 }}>
                  <CenteredTypography variant="h6">
                    Investment (excl. fees):{' '}
                    {numberToCurrencyString(
                      sumBy(
                        currentFundAllocation?.investments ?? [],
                        'adjustedNetInvestmentExFee',
                      ),
                    )}
                  </CenteredTypography>

                  {anyInvestorsOverAllocated && (
                    <Tooltip
                      arrow
                      title="This allocation requires unpaid committed capital to be paid by some investors prior to investment. Please request unpaid funds from investors before completing this allocation."
                    >
                      <StyledErrorOutline color="error" />
                    </Tooltip>
                  )}
                </CenteredGrid>
                <CenteredGrid
                  justifyContent="flex-end"
                  size={{ xs: 7, sm: 12, md: 2 }}
                >
                  {currentFundAllocation.finalDeal && (
                    <StyledChipContainer>
                      <Chip small variant="success">
                        Final deal
                      </Chip>
                    </StyledChipContainer>
                  )}

                  {!isExistingAllocation &&
                    !isDraftAllocation &&
                    (hasAnyError ? (
                      <StyledErrorOutline color="error" />
                    ) : (
                      <StyledCheckCircleOutline color="primary" />
                    ))}
                </CenteredGrid>
              </GridContainer>
            </AccordionHeader>

            <AccordionBody>
              {((isEditing && currentFundAllocation.isFullyDeployed) ||
                isDraftAllocation ||
                fundIdsInDraftAllocation.includes(
                  fundAllocation.trancheId,
                )) && (
                <DeployStatusBar>
                  <FundEditHeaderbar
                    fundAllocation={currentFundAllocation}
                    fundIndex={fundIndex}
                    setValue={setValue}
                    isEditing={isEditing}
                    isDraftAllocation={isDraftAllocation}
                    fundIdsInDraftAllocation={fundIdsInDraftAllocation}
                  />
                </DeployStatusBar>
              )}
              <AppTable
                columns={columns}
                tablebody={fundAllocation.investments}
                TableFooter={
                  <EditedTotals
                    fundIndex={fundIndex}
                    originalFundAllocations={originalFundAllocations}
                    watchedValues={watchedValues}
                    formFundAllocations={formFundAllocations}
                    isEditing={canEdit}
                    theme={theme}
                  />
                }
              />
              {isEditing && canEdit && (
                <FinalDealToggleBar
                  fundIndex={fundIndex}
                  finalDeal={
                    currentFundAllocation.finalDeal ?? fundAllocation.finalDeal
                  }
                  setValue={setValue}
                />
              )}
            </AccordionBody>
          </StyledAccordion>
        );
      })}
      <StyledTotalsRow>
        <GridContainer justifyContent="space-between">
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Typography variant="body2" textAlign="center">
              Total shares allocated:{' '}
              {numberToDisplayString(
                getTotalValuesForField(
                  formFundAllocations,
                  watchedValues,
                  'finalShareholding',
                ),
              )}
              /
              {numberToDisplayString(
                sumBy(
                  formFundAllocations.map((fund, fundIndex) => {
                    const currentSharesAllocated = sumBy(
                      watchedValues.fundAllocations?.[fundIndex]?.investments ??
                        [],
                      'finalShareholding',
                    );
                    return {
                      targetShares: Math.max(
                        currentSharesAllocated,
                        fund.targetShares,
                      ),
                    };
                  }),
                  'targetShares',
                ),
              )}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Typography variant="body2" textAlign="center">
              Total investors in allocation:{' '}
              {numberToDisplayString(
                sumBy(
                  formFundAllocations.map((_fund, fundIndex) => ({
                    investmentsAllocatedTo: (
                      watchedValues.fundAllocations?.[fundIndex]?.investments ??
                      []
                    ).filter(
                      (investment) => (investment.finalShareholding ?? 0) > 0,
                    ).length,
                  })),
                  'investmentsAllocatedTo',
                ),
              )}
              /
              {numberToDisplayString(
                sumBy(formFundAllocations, 'investmentsInTranche'),
              )}
            </Typography>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <Typography variant="body2" textAlign="center">
              Total investment (excl. fees):{' '}
              {numberToCurrencyString(
                getTotalValuesForField(
                  formFundAllocations,
                  watchedValues,
                  'adjustedNetInvestmentExFee',
                ),
              )}
            </Typography>
          </Grid>
        </GridContainer>
      </StyledTotalsRow>
    </PageSection>
  );
};

export default FundSection;
